import { API_CACHE_KEYS } from "api/cacheKeys";
import { useCustomMutation } from "api/useCustomMutation";
import { useCustomQuery } from "api/useCustomQuery";
import { JANZU_URL } from "config/config";
import { useAppDispatch } from "hooks/store";
import { useUser } from "hooks/user/useUser";
import { setAuthUser } from "store/slices/Auth/authSlice";
import { User, UserProfile } from "types/users";

import { BaseQueryOptions, CachePolicy } from "../types";

// Hook for fetching user profile
export const useGetUserProfile = (
  options: BaseQueryOptions<UserProfile> = {}
) => useCustomQuery<UserProfile>(
  API_CACHE_KEYS.user.profile(),
  `${JANZU_URL}device-api/profile/`,
  {
    retry: 2,
    enabled: true,
    refetchOnMount: true,
    ...options,
  }
);

// Hook for updating user profile with auth user update logic
export const useUpdateUserProfile = () => {
  const user = useUser();
  const dispatch = useAppDispatch();

  const { mutateAsync, isLoading, error } = useCustomMutation<UserProfile, Partial<UserProfile>>(
    `${JANZU_URL}device-api/profile/`,
    "PATCH",
    {
      cachePolicy: CachePolicy.NO_CACHE,
    }
  );

  const updateProfile = async (data: Partial<UserProfile>) => {
    const userProfile = await mutateAsync(data);

    if (userProfile) {
      // Update the auth user in the store with the new profile data
      const updatedUser: User = {
        ...user,
        userprofile: userProfile,
      };
      dispatch(setAuthUser(updatedUser));
    }
    return userProfile;
  };

  return {
    mutateAsync: updateProfile,
    isLoading,
    error,
  };
};

// Combined hook that provides both get and update operations
export const useUserProfile = (options: BaseQueryOptions<UserProfile> = {}) => {
  const updateUserProfileMutation = useUpdateUserProfile();
  const getUserProfileQuery = useGetUserProfile(options);

  return {
    // Get operations
    userProfile: getUserProfileQuery.data,
    getUserProfile: getUserProfileQuery.refetch,
    isLoadingProfile: getUserProfileQuery.isFetching,
    profileError: getUserProfileQuery.error,

    // Update operations
    updateUserProfile: updateUserProfileMutation.mutateAsync,
    isUpdating: updateUserProfileMutation.isLoading,
    updateError: updateUserProfileMutation.error,
  };
}; 